/**
 * Route-fork PR-R4: Client Portal surface (`/portal/*`).
 *
 * Phase 1 ships six placeholder pages + the token-auto-login landing.
 * Every navigable route gates on `portal.access` (seeded by PR-R0; the
 * Client Admin / Store Manager / Distributor Manager / Sales Executive /
 * Super Admin role grants currently include this cap -- see
 * `api/src/seeders/v2/capability.py`). The invite/auto-login route is
 * unguarded by design: it issues a session FROM a URL token (it replaced
 * the legacy `/admin-buyer-dashboard/:id` landing, deleted with the Admin
 * Buyer dashboard, card #566).
 *
 * Phase 4 (per the 2026-05-29 re-plan) fills in the placeholder pages
 * with the real Client Portal UI. This file is structured so that swap
 * is a per-route component change rather than a route-table rewrite.
 */

import { requireCapability } from "../requireCapability";

import Overview from "@/pages/portal/Overview.vue";
import Orders from "@/pages/portal/Orders.vue";
import NewOrder from "@/pages/portal/NewOrder.vue";
import Approvals from "@/pages/portal/Approvals.vue";
import Budget from "@/pages/portal/Budget.vue";
import Allowance from "@/pages/portal/Allowance.vue";
import Team from "@/pages/portal/Team.vue";
import Settings from "@/pages/portal/Settings.vue";
import AutoLogin from "@/pages/portal/AutoLogin.vue";
// Phase F5 / ADR 0050: first-time Store Owner landing page after the
// invitation-accept flow mints a session.
import Welcome from "@/pages/portal/Welcome.vue";
// Personal Profile page -- the ONE shared component, mounted in the
// portal's own chrome (card https://trello.com/c/BxskXBad).
import PersonalProfilePage from "@/pages/PersonalProfilePage.vue";

// All guarded routes share `portal.access` as the entry cap. Phase 4 may
// layer additional sub-caps (e.g. `portal.read_own_team` on /portal/team)
// once the real pages land; for Phase 1 placeholders the single entry
// gate is enough.
const PORTAL_CAP = "portal.access";

// Auth-failure redirect: unauthenticated /portal/* hits land on /login.
// Phase 4 will likely point this at /portal/login (PR-R1 reserved that
// path in `requireToken.js` as a public bypass) once the dedicated portal
// sign-in page exists.
function portalGuard() {
    return requireCapability(PORTAL_CAP, "/login");
}

// Add New Order (card #566) needs BOTH the portal entry cap and order.create:
// a Client Admin holds both, a Store Manager only the first. One composed
// guard (not a guard array) so the route keeps the same single-function
// beforeEnter shape as every other portal route. A caller with portal access
// but no order.create goes back to the Orders list, not to /login.
const ORDER_CREATE_CAP = "order.create";

function portalOrderCreateGuard() {
    const portal = portalGuard();
    const create = requireCapability(ORDER_CREATE_CAP, "/portal/orders");
    return (to, from, next) =>
        portal(to, from, (redirect) =>
            redirect === undefined ? create(to, from, next) : next(redirect),
        );
}

const portalRoutes = [
    {
        // Portal landing -- renders Overview directly at the base path,
        // matching the distributor app (store list at /distributor/) and the
        // SA app (dashboard at /admin). Previously rendered PortalComingSoon,
        // a useless empty placeholder. postLoginRedirect.js + AutoLogin.vue
        // already target the bare "/portal" path, so they inherit this for
        // free.
        path: "/portal",
        name: "portal_overview",
        component: Overview,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },
    {
        // Canonicalize the old /portal/overview path to the base "/portal"
        // landing so the existing sidebar link (PortalLayout.vue) keeps
        // working without a duplicate route-name collision.
        path: "/portal/overview",
        redirect: { name: "portal_overview" },
    },
    {
        path: "/portal/orders",
        name: "portal_orders",
        component: Orders,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },
    {
        // Staff order entry for a Client Admin (card #566), moved here from
        // the deleted legacy Admin Buyer dashboard.
        path: "/portal/orders/new",
        name: "portal_new_order",
        component: NewOrder,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalOrderCreateGuard(),
    },
    {
        path: "/portal/approvals",
        name: "portal_approvals",
        component: Approvals,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },
    {
        // GH #3088 -- the route the approval email links to.
        //
        // Approvals used to be emailed as `/approve/<token>`, a PUBLIC
        // page where the token WAS the authentication. That made
        // approving the spending of a single-use credential, so a
        // design with several sizes burned its link on the first one.
        // This route is the replacement: an ordinary portal page behind
        // the SAME `portal.access` gate as the rest of the surface. A
        // signed-out recipient is bounced to login carrying `?next=`
        // and lands back here (requireToken.loginRedirectFor).
        //
        // Renders the Approvals component itself rather than a separate
        // detail page: one approval surface, one set of controls, no
        // second implementation to drift. The component opens the
        // review panel for `:id` on mount.
        path: "/portal/approvals/:id",
        name: "portal_approval_detail",
        component: Approvals,
        meta: {
            layout: "portal",
            requiredCap: PORTAL_CAP,
            // Deep-link auto-impersonation (router/deeplinkImpersonation.js):
            // the approval email links here, so an SA or the client's own
            // distributor opening it is seated as the owning client first.
            deeplinkOwner: { type: "portal_approval", param: "id" },
        },
        beforeEnter: portalGuard(),
    },
    {
        path: "/portal/budget",
        name: "portal_budget",
        component: Budget,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },
    {
        path: "/portal/allowance",
        name: "portal_allowance",
        component: Allowance,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },
    {
        path: "/portal/team",
        name: "portal_team",
        component: Team,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },
    {
        // Personal Profile (card https://trello.com/c/BxskXBad) -- the ONE
        // shared page, mounted in the portal chrome and reached from the
        // profile icon beside Log out.
        //
        // Deliberately NOT gated on PORTAL_CAP: this is the signed-in
        // user's OWN account, not a portal resource. A portal user who
        // somehow lacks portal.access still needs to be able to change
        // their own password. The backend is self-scoped to the bearer
        // token, so there is nothing here a capability could protect.
        path: "/portal/profile",
        name: "portal_profile",
        component: PersonalProfilePage,
        meta: { layout: "portal" },
    },
    {
        // Settings: the Company section, where the client's owner (Client
        // Admin) renames its own company (Jake, 2026-10-27). The mock-data
        // Settings page cut for beta on 2026-07-02 is NOT back; this page
        // reads and writes real data. Entry is the portal gate like every
        // other portal page; renaming is enforced server-side
        // (portal.manage_company) and everyone else sees the name read-only.
        path: "/portal/settings",
        name: "portal_settings",
        component: Settings,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },

    // /portal/welcome -- first-time landing after invitation accept
    // (ADR 0050 / Phase F5). Same portal.access gate as the rest of
    // the portal surface so a recipient who somehow lands here
    // without a session (bookmark + cleared storage) gets bounced to
    // /portal/login rather than seeing the welcome chrome with a
    // dead store state.
    {
        path: "/portal/welcome",
        name: "portal_welcome",
        component: Welcome,
        meta: { layout: "portal", requiredCap: PORTAL_CAP },
        beforeEnter: portalGuard(),
    },

    // /portal/login -- NOT a page (card #768). It used to be a demo-only
    // form (mock "Acme Industries" branding, DEMO DATA banner, no auth call).
    // It is now an alias for the portal home, so a visitor is treated exactly
    // like one who opened any protected /portal page: the global requireToken
    // guard sends a signed-out user to /login?next=/portal (the standard,
    // validated post-login mechanism), a signed-in client lands on /portal,
    // and a signed-in non-client gets the portal guard's usual /login bounce.
    // The path is deliberately NOT in requireToken's public allowlist.
    {
        path: "/portal/login",
        name: "portal_login",
        redirect: "/portal",
    },

    // /portal/invite/:token -- token-auto-login landing. Public route by
    // design: the token IS the credential. NO beforeEnter guard here;
    // the auth gate would deny unauthenticated visitors. The component
    // swaps the URL token for a session via /auth/loginClientPortalWithToken
    // and then routes to /portal.
    //
    // `meta.layout: 'public'`, NOT 'portal': PortalLayout fires authenticated
    // reads on mount (/portal/stores, platform settings). Signed out, those
    // 401 and the global 401 interceptor logs out and redirects to /login,
    // which defeats the auto-login (the close-reminder email link, #566).
    // The landing has no session yet; /portal mounts the portal shell after
    // the token is redeemed.
    {
        path: "/portal/invite/:token",
        name: "portal_invite_autologin",
        component: AutoLogin,
        meta: { layout: "public" },
    },
];

export default portalRoutes;
